import { useMutation } from '@apollo/client';
import React from 'react';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import {
  Caption,
  Combobox,
  ComboboxDropdown,
  ComboboxEmpty,
  ComboboxOption,
  ComboboxSelectButton,
  HStack,
  Icon,
  NewButton,
  Text,
  VStack,
  useToast,
} from '@eluve/components';
import { tenantUsersFragment } from '@eluve/frontend-feature-tenants';
import {
  useIsTemplatePublisher,
  useTenantIdFromSession,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';

import { templateOrgsFragment, templateUsersFragment } from '../operations';

import {
  shareTemplateWithUserMutation,
  unshareTemplateWithUserMutation,
} from './publishing.operations';

export interface PublishTemplateToUsersProps {
  templateId: string;
}

export const PublishTemplateToUsers: React.FC<PublishTemplateToUsersProps> = ({
  templateId,
}) => {
  const toast = useToast();
  const tenantId = useTenantIdFromSession();
  const userId = useUserIdFromSession();
  const isUserTemplatePublisher = useIsTemplatePublisher();

  const isOrgTemplatePublisher = useIsFeatureFlagEnabled('TEMPLATE_PUBLISHING');

  const isTemplatePublisher = isOrgTemplatePublisher && isUserTemplatePublisher;

  const templateUsersData = useCompleteFragment({
    fragment: templateUsersFragment,
    key: {
      id: templateId,
    },
  });

  const templateOrgData = useCompleteFragment({
    fragment: templateOrgsFragment,
    key: {
      id: templateId,
    },
  });

  const { tenantUsers } = useCompleteFragment({
    fragment: tenantUsersFragment,
    key: {
      id: tenantId ?? '',
    },
  });

  const [shareTemplateWithUser] = useMutation(shareTemplateWithUserMutation);
  const [unshareTemplateWithUser] = useMutation(
    unshareTemplateWithUserMutation,
  );

  const orgSettings = templateOrgData?.tenant_llm_output_templates.find(
    (t) => t.tenantId === tenantId,
  );
  const isSharedWithOrg = orgSettings?.isEnabled ?? false;

  const usersWithAccess =
    templateUsersData?.users_llm_output_templates.map((u) => u.userId) ?? [];

  const searchableUsers = tenantUsers.filter(
    (tenantUser) => !usersWithAccess.includes(tenantUser.userId),
  );

  const handleGrantAccess = async (selectedUserId: string) => {
    await shareTemplateWithUser({
      variables: {
        templateId,
        tenantId: tenantId!,
        userId: selectedUserId,
      },
      optimisticResponse: {
        insertUsersLlmOutputTemplatesOne: {
          __typename: 'UsersLlmOutputTemplates' as const,
          llmOutputTemplateId: templateId,
          tenantId: tenantId!,
          userId: selectedUserId,
          createdAt: new Date().toISOString(),
        },
      },
      onCompleted: () => {
        cacheUtils.updateFragment(
          {
            fragment: templateUsersFragment,
            key: {
              id: templateId,
            },
          },
          (existing) => {
            if (!existing) {
              return existing;
            }

            return {
              ...existing,
              users_llm_output_templates: [
                ...existing.users_llm_output_templates,
                {
                  __typename: 'UsersLlmOutputTemplates' as const,
                  llmOutputTemplateId: templateId,
                  tenantId: tenantId!,
                  userId: selectedUserId,
                  createdAt: new Date().toISOString(),
                },
              ],
            };
          },
        );

        toast.success('Sharing updated');
      },
    });
  };

  const handleRevokeAccess = async (selectedUserId: string) => {
    // Implement the logic to revoke access from the selected user
    await unshareTemplateWithUser({
      variables: {
        templateId,
        tenantId: tenantId!,
        userId: selectedUserId,
      },
      optimisticResponse: {
        deleteUsersLlmOutputTemplates: {
          __typename: 'UsersLlmOutputTemplatesMutationResponse' as const,
          affectedRows: 1,
        },
      },
      onCompleted: () => {
        cacheUtils.updateFragment(
          {
            fragment: templateUsersFragment,
            key: {
              id: templateId,
            },
          },
          (existing) => {
            if (!existing) {
              return existing;
            }

            return {
              ...existing,
              users_llm_output_templates:
                existing.users_llm_output_templates.filter(
                  (u) => u.userId !== selectedUserId,
                ),
            };
          },
        );
        toast.success('Sharing updated');
      },
    });
  };

  return (
    <VStack gap={5}>
      {isTemplatePublisher && (
        <Combobox disabled={isSharedWithOrg}>
          <ComboboxSelectButton className="w-full" size="sm">
            <HStack className="px-2 py-1">
              <Icon name="UserRoundPlus" size="xs" />
              <Text size="md" color="secondary">
                Add users
              </Text>
            </HStack>
          </ComboboxSelectButton>
          <ComboboxDropdown searchPlaceholder="Search users">
            <ComboboxEmpty>
              <HStack
                className="p-2 text-sm text-text-tertiary"
                justify="center"
              >
                No users available to add.
              </HStack>
            </ComboboxEmpty>
            {searchableUsers
              .sort((a, b) =>
                (a.user?.firstName ?? '').localeCompare(
                  b.user?.firstName ?? '',
                ),
              )
              .map(({ user, userId }) => (
                <ComboboxOption
                  key={userId}
                  onSelect={() => handleGrantAccess(userId)}
                >
                  <Text size="md" color="secondary">
                    {`${user.firstName} ${user.lastName} (${user.email})`}
                  </Text>
                </ComboboxOption>
              ))}
          </ComboboxDropdown>
        </Combobox>
      )}

      <VStack>
        <Caption size="md" weight="semibold" color="tertiary">
          SHARED WITH
        </Caption>

        {usersWithAccess.length === 0 && (
          <Text size="md" color="tertiary">
            No users added
          </Text>
        )}

        {usersWithAccess.map((userWithAccess) => {
          const tenantUser = tenantUsers.find(
            (tu) => tu.userId === userWithAccess,
          );
          if (!tenantUser) {
            return null;
          }
          const { user } = tenantUser;
          return (
            <HStack key={userWithAccess} justify="between">
              <Text size="md" color="secondary">
                {`${user.firstName} ${user.lastName}`}{' '}
                {userWithAccess === userId ? (
                  <span color="text-text-tertiary">(You)</span>
                ) : (
                  ''
                )}
              </Text>

              {isTemplatePublisher && (
                <NewButton
                  type="outline"
                  size="s"
                  icon={{ name: 'Trash' }}
                  onClick={() => handleRevokeAccess(userWithAccess)}
                  disabled={isSharedWithOrg}
                />
              )}
            </HStack>
          );
        })}
      </VStack>
    </VStack>
  );
};
